import * as Sentry from "@sentry/nuxt";
import { isAxiosError } from "axios";

try {
  // this is the client-side configuration and is read and handled by the @sentry/nuxt module
  // the server-side implementation is handled by the server/plugins/sentry.ts file as the module doesn't have cloudflare support yet
  Sentry.init({
    dsn: useRuntimeConfig().public.sentry.dsn,
    environment: useRuntimeConfig().public.sentry.environment,
    release: useRuntimeConfig().public.sentry.release,
    tunnel: useRuntimeConfig().public.sentry.tunnel,
    enabled: useRuntimeConfig().public.sentry.enabled,
    tracesSampleRate: 0.001,
    tracePropagationTargets: [
      /^https:\/\/api\.mwsacc\.com/,
      /^https:\/\/api\.matchwornshirt\.com/,
      /^https:\/\/api\.mwsprod\.com/,
      /^\//,
    ],
    denyUrls: [/^safari-extension:\/\//i, /extensions\//i, /^chrome:\/\//i],
    allowUrls: [
      /https?:\/\/((www)\.)?matchwornshirt\.com/,
      /https?:\/\/((www)\.)?mwsacc\.com/,
    ],
    // Only send error replays to Sentry.
    replaysOnErrorSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    ignoreErrors: [
      "NavigationDuplicated",
      "Redirected when going from",
      "instantSearchSDKJSBridgeClearHighlight",
      "Avoided redundant navigation",
      "Navigation cancelled",
      "Request failed with status code 404",
      "redeclaration of const hideMyLocation",
      "undefined is not an object (evaluating 'a.O')",
      "ResizeObserver loop completed with undelivered notifications",
      "undefined is not an object (evaluating 'this.touchendListener')", // https://github.com/Adyen/adyen-web/issues/2576
      "zaloJSV2",
      "undefined is not an object (evaluating 'a.L')", // we don't know where this comes from but it doesn't seem to cause a visible problem

      // chunk errors (Nuxt handles them already: https://nuxt.com/docs/getting-started/error-handling#errors-with-js-chunks)
      "Importing a module script failed",
      "Failed to fetch dynamically imported module",
      "error loading dynamically imported module",
      "Unable to preload CSS",
      "undefined is not an object (evaluating 'A.default')",
      "undefined is not an object (evaluating 'r.default')",
      'Couldn\'t resolve component "default" at',

      "timeout exceeded",
      "Cannot redefine property: googletag", // probably adblocker: https://github.com/shadcn-ui/ui/issues/2837#issuecomment-1962884525
      "Object Not Found Matching Id", // outlook scanning the link: https://github.com/getsentry/sentry-javascript/issues/3440#issuecomment-865857552
      "onElementTextTranslated", // Microsoft Edge translation
      "<unknown>", // this error seems to happen after failing google translate requests

      // Android WebView errors
      "Java Object is gone",
      "Java bridge method invocation error",

      // Custom tracking error on Liverpool client
      "Event `CustomEvent` (type=unhandledrejection) captured as promise rejection",

      // 404 pages
      "404 page not found",
    ],
    beforeSend: (event, hint) => {
      if (!isAxiosError(hint.originalException)) {
        return event;
      }

      const axiosErrorMessages = getErrorMessage(hint.originalException, {
        allowRawErrorMessages: true,
      });

      let connection = "unknown";
      if (import.meta.client && window !== undefined && "navigator" in window) {
        connection = window.navigator.onLine ? "online" : "offline";
      }

      const hasEventExtra = typeof event?.extra === "object";
      event.extra = hasEventExtra
        ? {
            ...event.extra,
            connection,
            axiosErrorMessage: axiosErrorMessages.join("\n"),
          }
        : {
            connection,
            axiosErrorMessage: axiosErrorMessages.join("\n"),
          };

      return event;
    },
  });

  // Client side errors
  Sentry.setTag("type", "client");
  // Triggered within the framework
  Sentry.setTag("origin", "framework");
} catch {
  // if Sentry fails to initialize, we don't want to break the app
}
